import React, {createContext, useCallback, useContext, useState, ReactNode} from "react";

type ToastType = "info" | "success" | "error" | "warning";

interface Toast {
    id: number;
    message: string;
    type: ToastType;
}

interface ConfirmState {
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
}

interface UiContextValue {
    showToast: (message: string, type?: ToastType) => void;
    confirm: (message: string) => Promise<boolean>;
    alert: (message: string) => void;
}

const UiContext = createContext<UiContextValue | undefined>(undefined);

export const useUi = (): UiContextValue => {
    const ctx = useContext(UiContext);
    if (!ctx) throw new Error("useUi must be used within UiProvider");
    return ctx;
};

let nextId = 1;

// Delovi poruke između para ** se prikazuju podebljano
// (npr. "**Napomena za trenere:** ostatak teksta")
const renderWithBold = (message: string): React.ReactNode => {
    const parts = String(message).split("**");
    if (parts.length === 1) return message;
    return parts.map((part, index) =>
        index % 2 === 1 ? <b key={index}>{part}</b> : <span key={index}>{part}</span>,
    );
};

export const UiProvider: React.FC<{children: ReactNode}> = ({children}) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
    const [alertState, setAlertState] = useState<{message: string; onClose: () => void} | null>(null);

    const showToast = useCallback((message: string, type: ToastType = "info") => {
        const id = nextId++;
        setToasts((prev) => [...prev, {id, message, type}]);
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 4000);
    }, []);

    const confirm = useCallback((message: string): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmState({
                message,
                onConfirm: () => {
                    setConfirmState(null);
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmState(null);
                    resolve(false);
                },
            });
        });
    }, []);

    const alert = useCallback((message: string) => {
        setAlertState({
            message,
            onClose: () => setAlertState(null),
        });
    }, []);

    const typeBg: Record<ToastType, string> = {
        info: "var(--primary)",
        success: "var(--highlight)",
        warning: "#e6c619",
        error: "var(--danger)",
    };

    const typeColor: Record<ToastType, string> = {
        info: "var(--text)",
        success: "var(--white)",
        warning: "var(--text)",
        error: "var(--white)",
    };

    return (
        <UiContext.Provider value={{showToast, confirm, alert}}>
            {children}

            {/* Toast Container */}
            <div style={{
                position: "fixed",
                top: 20,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2000,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                pointerEvents: "none",
                width: "90%",
                maxWidth: 400,
            }}>
                {toasts.map((t) => (
                    <div key={t.id} style={{
                        background: typeBg[t.type],
                        color: typeColor[t.type],
                        padding: "12px 16px",
                        borderRadius: 12,
                        boxShadow: "0 4px 16px rgba(59, 88, 85, 0.18)",
                        pointerEvents: "auto",
                        animation: "toastIn 0.25s ease",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        textAlign: "center",
                        whiteSpace: "pre-wrap",
                    }}>
                        {t.message}
                    </div>
                ))}
            </div>

            {/* Confirm Modal */}
            {confirmState && (
                <div style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(59, 88, 85, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2100,
                    padding: 16,
                }} onClick={() => confirmState.onCancel?.()}>
                    <div style={{
                        background: "var(--white)",
                        borderRadius: 16,
                        padding: 24,
                        maxWidth: 400,
                        width: "100%",
                        boxShadow: "0 10px 40px rgba(59, 88, 85, 0.25)",
                        textAlign: "center",
                    }} onClick={(e) => e.stopPropagation()}>
                        <p style={{margin: "0 0 20px 0", fontSize: "1rem", whiteSpace: "pre-wrap"}}>
                            {renderWithBold(confirmState.message)}
                        </p>
                        <div style={{display: "flex", gap: 12}}>
                            <button className="btn-secondary" style={{flex: 1}} onClick={() => confirmState.onCancel?.()}>
                                Ne
                            </button>
                            <button className="btn-primary" style={{flex: 1}} onClick={confirmState.onConfirm}>
                                Da
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Modal */}
            {alertState && (
                <div style={{
                    position: "fixed",
                    inset: 0,
                    background: "rgba(59, 88, 85, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 2100,
                    padding: 16,
                }} onClick={alertState.onClose}>
                    <div style={{
                        background: "var(--white)",
                        borderRadius: 16,
                        padding: 24,
                        maxWidth: 400,
                        width: "100%",
                        boxShadow: "0 10px 40px rgba(59, 88, 85, 0.25)",
                        textAlign: "center",
                    }} onClick={(e) => e.stopPropagation()}>
                        <p style={{margin: "0 0 20px 0", fontSize: "1rem", whiteSpace: "pre-wrap"}}>
                            {renderWithBold(alertState.message)}
                        </p>
                        <button className="btn-primary" onClick={alertState.onClose}>
                            U redu
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes toastIn {
                    from { opacity: 0; transform: translateY(-20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </UiContext.Provider>
    );
};
